/**
 * Jurídico: marcas e patentes e agenda jurídica (ADR 0026). Validações usadas na tela e no
 * servidor e a leitura das planilhas nacional e internacional do escritório (Tottal). Nenhuma
 * data é calculada aqui: vigência, prorrogação e proteção de uso vêm do órgão ou do escritório.
 */
import { z } from 'zod';
import { LEGAL_CONFIDENTIALITY } from './legal.ts';
import { importResolutions, looseText } from './legal-contracts.ts';
import { isKnownCountry } from './partners.ts';

export const IP_KINDS = [
  'trademark',
  'patent',
  'utility_model',
  'industrial_design',
  'software',
  'other',
] as const;
export const IP_PRESENTATIONS = [
  'word',
  'mixed',
  'figurative',
  'three_dimensional',
  'other',
] as const;
export const IP_STATUSES = [
  'filed',
  'under_examination',
  'published',
  'opposition',
  'office_action',
  'allowed',
  'granted',
  'refused',
  'appeal',
  'abandoned',
  'archived',
  'expired',
  'other',
] as const;
export type IpStatus = (typeof IP_STATUSES)[number];
/** Situações que pedem providência (oposição, exigência, recurso, deferida aguardando pagamento). */
export const IP_ATTENTION_STATUSES = ['opposition', 'office_action', 'appeal', 'allowed'] as const;
/** Situações encerradas: não entram na agenda nem nos alertas de vencimento. */
export const IP_CLOSED_STATUSES = ['refused', 'abandoned', 'archived', 'expired'] as const;
/** Vencimentos de marcas e patentes considerados próximos no painel (dias). */
export const IP_EXPIRING_DAYS = 180;

export const AGENDA_KINDS = [
  'renewal',
  'non_renewal_notice',
  'expiry',
  'use_proof',
  'office_response',
  'payment',
  'delivery',
  'report',
  'filing',
  'meeting',
  'follow_up',
  'other',
  // Contencioso (ADR 0064): audiência ou sessão e prazo processual (sempre informado e conferido).
  'hearing',
  'procedural_deadline',
] as const;
export const AGENDA_STATUSES = ['open', 'in_progress', 'done', 'cancelled'] as const;
export const AGENDA_PRIORITIES = ['normal', 'high', 'critical'] as const;
/**
 * De onde vem cada item da agenda: ação cadastrada ou data já gravada no registro (fim do
 * contrato, prazo para comunicar a não renovação, vencimento e prorrogação da marca, prazo da
 * demanda externa).
 */
export const AGENDA_SOURCES = [
  'action',
  'obligation',
  'contract_end',
  'contract_notice',
  'contract_adjustment',
  'ip_expiry',
  'ip_renewal',
  'matter_due',
] as const;
export type AgendaSource = (typeof AGENDA_SOURCES)[number];
/** Farol amarelo: vence em até tantos dias. */
export const AGENDA_ATTENTION_DAYS = 30;

/** Farol do prazo: vencido (vermelho), em até 30 dias (amarelo) ou depois (verde). */
export function agendaLight(dueDate: string, today: string): 'overdue' | 'soon' | 'later' {
  if (dueDate < today) return 'overdue';
  const limit = new Date(`${today}T00:00:00Z`);
  limit.setUTCDate(limit.getUTCDate() + AGENDA_ATTENTION_DAYS);
  return dueDate <= limit.toISOString().slice(0, 10) ? 'soon' : 'later';
}

/**
 * Prazo para comunicar a não renovação: o fim do contrato menos o aviso prévio do próprio
 * contrato (cláusula cadastrada, não regra legal). Sem fim ou sem aviso prévio, não há prazo.
 */
export function noticeDeadline(endDate: string | null, noticeDays: number | null): string | null {
  if (!endDate || noticeDays == null) return null;
  const d = new Date(`${endDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - noticeDays);
  return d.toISOString().slice(0, 10);
}

// Regiões e escritórios regionais (padrão WIPO ST.3) aceitos além dos países.
const IP_REGIONS = new Set(['EM', 'WO', 'OA', 'AP', 'EA', 'BX', 'GC']);
export function isIpCountry(code: string): boolean {
  return IP_REGIONS.has(code) || isKnownCountry(code);
}

// ----- Entradas -----

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const id = z
  .uuid()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const day = z
  .string()
  .regex(/^(\d{4}-\d{2}-\d{2})?$/)
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

export const legalIpAssetInput = z
  .object({
    kind: z.enum(IP_KINDS).default('trademark'),
    legalEntityId: z.uuid(),
    title: z.string().trim().min(1).max(300),
    presentation: z.enum(IP_PRESENTATIONS).nullable().optional(),
    niceClasses: z.array(z.number().int().min(1).max(45)).max(45).default([]),
    country: z
      .string()
      .trim()
      .toUpperCase()
      .refine(isIpCountry, 'País ou região inválido (use o código de duas letras, ex.: BR, US)'),
    office: text(60),
    processNumber: text(60),
    registrationNumber: text(60),
    holderName: text(300),
    status: z.enum(IP_STATUSES).default('filed'),
    statusDetail: text(500),
    filingDate: day,
    grantDate: day,
    validFrom: day,
    expiryDate: day,
    renewalStart: day,
    renewalEnd: day,
    useProtectionDate: day,
    specification: text(20000),
    nextSteps: text(10000),
    attorney: text(200),
    agentPartnerId: id,
    ownerId: id,
    costAmount: z
      .string()
      .regex(/^(\d{1,15}(\.\d{1,4})?)?$/, 'Use números com ponto (ex.: 1500.00)')
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    costCurrency: z
      .string()
      .regex(/^([A-Z]{3})?$/)
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    confidentiality: z.enum(LEGAL_CONFIDENTIALITY).default('internal'),
    notes: text(10000),
  })
  .refine((v) => (v.costAmount == null) === (v.costCurrency == null), {
    path: ['costCurrency'],
    message: 'Informe o valor e a moeda juntos',
  })
  .refine((v) => !v.validFrom || !v.expiryDate || v.expiryDate >= v.validFrom, {
    path: ['expiryDate'],
    message: 'O fim da vigência não pode ser antes do início',
  })
  .refine((v) => !v.renewalStart || !v.renewalEnd || v.renewalEnd >= v.renewalStart, {
    path: ['renewalEnd'],
    message: 'O fim da prorrogação não pode ser antes do início',
  });
export type LegalIpAssetInput = z.infer<typeof legalIpAssetInput>;

export const legalAgendaActionInput = z
  .object({
    contractId: id,
    matterId: id,
    ipAssetId: id,
    lawsuitId: id,
    kind: z.enum(AGENDA_KINDS),
    title: z.string().trim().min(1).max(300),
    description: text(10000),
    dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Informe o prazo'),
    priority: z.enum(AGENDA_PRIORITIES).default('normal'),
    status: z.enum(AGENDA_STATUSES).default('open'),
    ownerId: z.uuid('Escolha o responsável'),
    /** Responsável externo (ex.: o profissional do escritório). */
    externalOwner: text(200),
    /** Prazo processual: o responsável jurídico confirma que conferiu a data limite. */
    dueConfirmed: z.boolean().optional(),
  })
  .refine(
    (v) => [v.contractId, v.matterId, v.ipAssetId, v.lawsuitId].filter(Boolean).length === 1,
    {
      path: ['contractId'],
      message:
        'A ação precisa estar ligada a um contrato, uma demanda, uma marca/patente ou um processo',
    },
  );
export type LegalAgendaActionInput = z.infer<typeof legalAgendaActionInput>;

export const legalIpImportFile = z.object({
  fileName: z.string().trim().min(1).max(255),
  contentBase64: z.string().min(1).max(14_000_000),
  scope: z.enum(['national', 'international']),
  agentPartnerId: id,
  resolutions: importResolutions,
});
export const legalIpImportApply = legalIpImportFile.extend({
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
});

// ----- Planilhas do escritório (nacional e internacional) -----

/**
 * Nomes de coluna reconhecidos (comparados sem acento, maiúsculas nem pontuação). A planilha
 * nacional da Tottal traz uma linha por processo e classe; a internacional, um processo com várias
 * classes e os custos em dólar.
 */
export const IP_SHEET_COLUMNS = {
  title: ['marca', 'titulo', 'nome da marca', 'trademark', 'mark'],
  process: [
    'processo',
    'numero do processo',
    'n do processo',
    'numero do pedido',
    'pedido',
    'application number',
    'serial number',
  ],
  registration: ['numero do registro', 'n do registro', 'registration number'],
  classes: ['classe', 'classes', 'ncl', 'class', 'classes nice'],
  presentation: ['apresentacao', 'presentation'],
  nature: ['natureza'],
  kind: ['tipo', 'modalidade', 'tipo de registro'],
  holder: ['titular', 'holder', 'owner'],
  country: ['pais', 'country'],
  office: ['orgao', 'office'],
  status: ['descricao do ultimo despacho', 'ultimo despacho', 'estado da marca', 'situacao'],
  controlStatus: ['status'],
  filingDate: ['deposito', 'data do deposito', 'data do pedido', 'filing date'],
  grantDate: ['concessao', 'data da concessao', 'data do registro', 'registration date'],
  validFrom: ['vigencia inicio'],
  expiryDate: ['vigencia fim', 'validade', 'expiration date'],
  renewalStart: ['prorrogacao inicio'],
  renewalEnd: ['prorrogacao fim'],
  useProtection: ['protecao de uso'],
  specification: ['especificacao', 'specification'],
  nextSteps: ['providencias consideracoes', 'providencias', 'consideracoes'],
  attorney: ['procurador'],
  responsible: ['responsavel'],
  maintenance: ['manutencao'],
  figurative: ['elemento figurativo'],
} as const;
export type IpSheetField = keyof typeof IP_SHEET_COLUMNS | 'cost';

/** Localiza as colunas conhecidas; "Custos (USD)" informa também a moeda. */
export function ipSheetColumns(header: readonly (string | null)[]) {
  const map: Partial<Record<IpSheetField, number>> = {};
  let costCurrency: string | null = null;
  const ignored: string[] = [];
  header.forEach((raw, i) => {
    const name = looseText(raw ?? '');
    if (!name) return;
    const cost = /^custos?( ([a-z]{3}))?$/.exec(name);
    if (cost) {
      map.cost ??= i;
      costCurrency = cost[2]?.toUpperCase() ?? null;
      return;
    }
    const field = (Object.keys(IP_SHEET_COLUMNS) as (keyof typeof IP_SHEET_COLUMNS)[]).find((f) =>
      (IP_SHEET_COLUMNS[f] as readonly string[]).includes(name),
    );
    if (field && map[field] === undefined) map[field] = i;
    else ignored.push((raw ?? '').trim());
  });
  // Sem coluna de situação do órgão, o "STATUS" da planilha é a situação.
  if (map.status === undefined && map.controlStatus !== undefined) {
    map.status = map.controlStatus;
    delete map.controlStatus;
  }
  return { map, costCurrency: costCurrency as string | null, ignored };
}

/** Classes de Nice: "Ncl(11) 28" → [28]; "28, 35" → [28, 35]. */
export function ipClasses(value: string | null | undefined): number[] {
  const plain = (value ?? '').replace(/ncl\s*\(\s*\d+\s*\)/gi, ' ');
  const found = (plain.match(/\d{1,2}/g) ?? []).map(Number).filter((n) => n >= 1 && n <= 45);
  return [...new Set(found)].sort((a, b) => a - b);
}

const STATUS_RULES: [RegExp, IpStatus][] = [
  [/em tramite/, 'under_examination'],
  [/recurso nao provido|indeferid|refused|rejected|negad/, 'refused'],
  [/aguardando pagamento|deferid|allowed|allowance/, 'allowed'],
  [/concessao|registrad|registered|em vigor|concedid|prorrogad|\blive\b/, 'granted'],
  [/oposicao|opposition/, 'opposition'],
  [/exigencia|office action/, 'office_action'],
  [/recurso|appeal/, 'appeal'],
  [/abandon/, 'abandoned'],
  [/arquivad/, 'archived'],
  [/extint|caducad|expired|cancelad|nulidade/, 'expired'],
  [/publicad|published/, 'published'],
  [/exame|examination|pending|andamento|analise/, 'under_examination'],
  [/deposit|filed|protocolad/, 'filed'],
];
/** Situação do órgão a partir do texto do despacho ou do escritório; sem regra conhecida, null. */
export function ipStatus(value: string | null | undefined): IpStatus | null {
  const v = looseText(value ?? '');
  if (!v) return null;
  return STATUS_RULES.find(([re]) => re.test(v))?.[1] ?? null;
}

export function ipPresentation(value: string | null | undefined) {
  const v = looseText(value ?? '');
  if (!v) return null;
  if (/nominativ|word/.test(v)) return 'word';
  if (/mist|composite|combined/.test(v)) return 'mixed';
  if (/figurativ|logo|design/.test(v)) return 'figurative';
  if (/tridimensional|3d/.test(v)) return 'three_dimensional';
  return 'other';
}

export function ipKind(value: string | null | undefined): (typeof IP_KINDS)[number] {
  const v = looseText(value ?? '');
  if (/modelo de utilidade|utility/.test(v)) return 'utility_model';
  if (/patente|patent|invencao/.test(v)) return 'patent';
  if (/desenho industrial|industrial design/.test(v)) return 'industrial_design';
  if (/software|programa de computador/.test(v)) return 'software';
  return 'trademark';
}

const COUNTRY_NAMES: Record<string, string> = {
  brasil: 'BR',
  brazil: 'BR',
  'estados unidos': 'US',
  'estados unidos da america': 'US',
  eua: 'US',
  usa: 'US',
  'united states': 'US',
  'uniao europeia': 'EM',
  'european union': 'EM',
  euipo: 'EM',
  'reino unido': 'GB',
  'united kingdom': 'GB',
  inglaterra: 'GB',
  china: 'CN',
  japao: 'JP',
  japan: 'JP',
  canada: 'CA',
  mexico: 'MX',
  argentina: 'AR',
  chile: 'CL',
  colombia: 'CO',
  peru: 'PE',
  uruguai: 'UY',
  uruguay: 'UY',
  paraguai: 'PY',
  paraguay: 'PY',
  australia: 'AU',
  'nova zelandia': 'NZ',
  'new zealand': 'NZ',
  'coreia do sul': 'KR',
  'south korea': 'KR',
  india: 'IN',
  alemanha: 'DE',
  germany: 'DE',
  franca: 'FR',
  france: 'FR',
  espanha: 'ES',
  spain: 'ES',
  portugal: 'PT',
  italia: 'IT',
  italy: 'IT',
  suica: 'CH',
  switzerland: 'CH',
  singapura: 'SG',
  singapore: 'SG',
  internacional: 'WO',
  ompi: 'WO',
  wipo: 'WO',
  madrid: 'WO',
};
/** País (ou região) pelo nome ou código de duas letras; desconhecido, null. */
export function ipCountry(value: string | null | undefined): string | null {
  const raw = (value ?? '').trim();
  if (/^[A-Za-z]{2}$/.test(raw) && isIpCountry(raw.toUpperCase())) return raw.toUpperCase();
  return COUNTRY_NAMES[looseText(raw)] ?? null;
}

const OFFICES: Record<string, string> = {
  BR: 'INPI',
  US: 'USPTO',
  EM: 'EUIPO',
  GB: 'UKIPO',
  WO: 'WIPO',
  CN: 'CNIPA',
  JP: 'JPO',
  CA: 'CIPO',
  MX: 'IMPI',
  AR: 'INPI-AR',
  CL: 'INAPI',
  CO: 'SIC',
  PE: 'INDECOPI',
  UY: 'DNPI',
  AU: 'IP Australia',
  NZ: 'IPONZ',
  KR: 'KIPO',
  IN: 'CGPDTM',
  DE: 'DPMA',
  FR: 'INPI-FR',
  ES: 'OEPM',
  PT: 'INPI-PT',
  IT: 'UIBM',
  CH: 'IPI',
  SG: 'IPOS',
};
/** Órgão de registro mais comum do país (quando a planilha não informa). */
export function ipOfficeFor(country: string): string | null {
  return OFFICES[country] ?? null;
}

/** Titular sem a sigla de origem do INPI: "KRAKEN LTDA. (BR/MG)" → "kraken ltda". */
export function holderKey(value: string | null | undefined): string {
  return looseText((value ?? '').replace(/\(\s*[A-Za-z]{2}(\s*\/\s*[A-Za-z]{2})?\s*\)\s*$/, ''));
}
